import type { RetryPolicy } from '../types';
import { fetchWithTimeout } from './fetchWithTimeout';

interface RetryMetadata {
  attempt: number;
  reason: 'status' | 'error';
  status?: number;
  error?: Error;
}

interface RetryHooks {
  onRetry?: (metadata: RetryMetadata) => void;
}

function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    (error as { name?: unknown }).name === 'AbortError'
  );
}

export async function fetchWithRetry(
  url: string,
  init: RequestInit & { timeoutMs?: number },
  retryPolicy?: RetryPolicy,
  hooks?: RetryHooks
): Promise<Response> {
  const retries = retryPolicy?.retries ?? 0;
  const backoffMs = retryPolicy?.backoffMs ?? 500;
  const retryStatuses = retryPolicy?.retryOnStatuses ?? [429, 500, 502, 503, 504];

  let attempt = 0;
  while (true) {
    try {
      const res = await fetchWithTimeout(url, init);
      if (!res.ok && attempt < retries && retryStatuses.includes(res.status)) {
        attempt += 1;
        hooks?.onRetry?.({ attempt, reason: 'status', status: res.status });
        await new Promise(resolve => setTimeout(resolve, Math.min(backoffMs * attempt, 10_000)));
        continue;
      }
      return res;
    } catch (error) {
      if (isAbortError(error) || init.signal?.aborted) {
        throw error;
      }
      if (attempt < retries) {
        attempt += 1;
        hooks?.onRetry?.({
          attempt,
          reason: 'error',
          error: error instanceof Error ? error : new Error(String(error)),
        });
        await new Promise(resolve => setTimeout(resolve, Math.min(backoffMs * attempt, 10_000)));
        continue;
      }
      throw error;
    }
  }
}
